import React, { FC, Fragment, useState } from 'react';
import { AlertDialog } from 'DesignSystem';
import { Archive } from '@styled-icons/bootstrap/Archive';
import { BarChart } from '@styled-icons/bootstrap/BarChart';
import { Edit } from '@styled-icons/boxicons-solid/Edit';
import { Files } from '@styled-icons/bootstrap/Files';
import useProductActions from 'FeatureModules/ProductPage/hooks/useProductActions';
import ProductTable from 'FeatureModules/ProductPage/Components/Tables/ProductTable';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';

interface IActiveProductTableProps {
  products: IProduct[];
  hasNextPage: boolean;
  onPageEndReached: () => void;
}

const ActiveProductTable: FC<IActiveProductTableProps> = ({
  products,
  hasNextPage,
  onPageEndReached,
}) => {
  const { handleDuplicateProduct, goToProductAnalytics, handleArchiveProduct, handleEditProduct } =
    useProductActions();

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);

  const handleOpenArchiveDialog = (product: IProduct) => {
    setArchiveDialogOpen(true);
    setSelectedProduct(product);
  };

  const handleCloseArchiveDialog = () => {
    setArchiveDialogOpen(false);
  };

  const handleArchive = () => {
    if (!selectedProduct) return;

    handleArchiveProduct(selectedProduct.id);
    setArchiveDialogOpen(false);
  };

  return (
    <>
      <ProductTable
        data-testid="active-product-table"
        products={products}
        hasNextPage={hasNextPage}
        onPageEndReached={onPageEndReached}
        actions={[
          {
            icon: <Archive size="18" />,
            tooltip: 'Archive product',
            onClick: handleOpenArchiveDialog,
            dataCy: 'archive-product-btn',
          },
          {
            icon: <Files size="17" />,
            tooltip: 'Duplicate product page',
            onClick: handleDuplicateProduct,
            dataCy: 'duplicate-product-btn',
          },
          {
            icon: <Edit size="17" />,
            tooltip: 'Edit product page',
            onClick: product => handleEditProduct(product),
          },
          {
            icon: <BarChart size="18" />,
            tooltip: 'View analytics',
            onClick: ({ id }) => goToProductAnalytics(id),
          },
        ]}
      />
      <AlertDialog
        onClose={handleCloseArchiveDialog}
        title="Archive product"
        open={archiveDialogOpen}
        text="Are you sure you want to archive this product? Your product will not be available anymore to any visits or QR code scans. You can unarchive your product and republish it."
        onSubmit={handleArchive}
        submitText="Yes, Archive"
        onCancel={handleCloseArchiveDialog}
      />
    </>
  );
};

export default ActiveProductTable;
